import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Full-screen image viewer with tap/click-to-zoom, pinch-to-zoom (mobile),
// wheel-zoom (desktop), drag-to-pan and optional prev/next navigation.
export const ImageLightbox = ({ open, images = [], index = 0, altFor, onClose, onIndexChange }) => {
  const [current, setCurrent] = useState(index);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinchStart = useRef(null);
  const panStart = useRef(null);

  useEffect(() => { setCurrent(index); }, [index, open]);

  const reset = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, []);
  useEffect(() => { reset(); }, [current, reset]);

  const goTo = useCallback((next) => {
    if (!images.length) return;
    const wrapped = (next + images.length) % images.length;
    setCurrent(wrapped);
    onIndexChange?.(wrapped);
  }, [images.length, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') goTo(current + 1);
      if (e.key === 'ArrowLeft') goTo(current - 1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [open, current, goTo, onClose]);

  if (!open || !images.length) return null;

  const toggleZoom = () => {
    if (scale > 1) reset();
    else setScale(2.5);
  };

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [p1, p2] = [...pointers.current.values()];
      pinchStart.current = { d: dist(p1, p2), scale };
    } else if (scale > 1) {
      panStart.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      const [p1, p2] = [...pointers.current.values()];
      const next = clamp(pinchStart.current.scale * (dist(p1, p2) / pinchStart.current.d), MIN_SCALE, MAX_SCALE);
      setScale(next);
      if (next === 1) setOffset({ x: 0, y: 0 });
    } else if (panStart.current && scale > 1) {
      setOffset({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;
  };

  const onWheel = (e) => {
    const next = clamp(scale - e.deltaY * 0.002, MIN_SCALE, MAX_SCALE);
    setScale(next);
    if (next === 1) setOffset({ x: 0, y: 0 });
  };

  const src = images[current];
  const alt = altFor ? altFor(current) : `Image ${current + 1}`;

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center" data-testid="image-lightbox" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" aria-label="Close" data-testid="lightbox-close">
        <X className="w-6 h-6" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); toggleZoom(); }} className="absolute top-4 left-4 z-20 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" aria-label={scale > 1 ? 'Zoom out' : 'Zoom in'} data-testid="lightbox-zoom-toggle">
        {scale > 1 ? <ZoomOut className="w-6 h-6" /> : <ZoomIn className="w-6 h-6" />}
      </button>

      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); goTo(current - 1); }} className="absolute left-3 md:left-6 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" aria-label="Previous image" data-testid="lightbox-prev">
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); goTo(current + 1); }} className="absolute right-3 md:right-6 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" aria-label="Next image" data-testid="lightbox-next">
            <ChevronRight className="w-7 h-7" />
          </button>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 text-white/80 text-sm font-medium bg-black/40 px-3 py-1 rounded-full" data-testid="lightbox-counter">
            {current + 1} / {images.length}
          </span>
        </>
      )}

      <div
        className="relative w-full h-full flex items-center justify-center p-8 md:p-16 touch-none select-none"
        style={{ cursor: scale > 1 ? 'grab' : 'zoom-in' }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={toggleZoom}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="max-w-full max-h-full object-contain rounded-lg transition-transform duration-100 will-change-transform"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
          data-testid="lightbox-image"
        />
      </div>
    </div>,
    document.body
  );
};

export default ImageLightbox;
